import React from "react";
import { Flex, Text, SimpleGrid, Box, AspectRatio } from "@chakra-ui/react";
import Image from "next/legacy/image";
import { type GetStaticProps, type NextPage } from "next";
import {
  type CaseStudySimple,
  type Page as SanityPage,
  type Customer,
  getPage,
  getSimpleCaseStudies,
  getAnnouncementBanner,
  type Banner,
  getStackedLogos,
} from "lib/sanity";
import { Section } from "components/section";
import { Page } from "components/page";
import { CustomersHero } from "components/customers-hero";
import { CustomerCard } from "components/customer-card";
import { ResourceCard } from "components/resource-card";
import { getPageMetaImage } from "utils";
import { StackedLogos } from "components/logos";
import heroImage from "../../../public/assets/customers/hero.png";

type PageProps = {
  page: SanityPage;
  heroCaseStudy: CaseStudySimple;
  featuredCaseStudies: CaseStudySimple[];
  remainingCaseStudies: CaseStudySimple[];
  customerRows: Customer[][];
  announcementBanner?: Banner;
};

const Customers: NextPage<Readonly<PageProps>> = ({
  page,
  heroCaseStudy,
  featuredCaseStudies,
  remainingCaseStudies,
  customerRows,
  announcementBanner,
}) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <CustomersHero
        title={page.title}
        description={page.description}
        image={heroImage}
        imageAlt={`${heroCaseStudy.customerName} activates their data with Hightouch.`}
        actionTitle={`Read the ${heroCaseStudy.customerName} story`}
        actionHref={`/customers/${heroCaseStudy.customerSlug}`}
      />

      <StackedLogos title="" customerRows={customerRows} />

      <Section center>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={12} width="100%">
          {featuredCaseStudies.map((caseStudy) => (
            <CustomerCard key={caseStudy.title} {...caseStudy} />
          ))}
        </SimpleGrid>
      </Section>

      <Section center>
        <Text as="h2" textStyle="h4" mb={{ base: 12, md: 16 }}>
          More stories
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} width="100%" justifyItems="center" gap={12}>
          {remainingCaseStudies.map((caseStudy) => (
            <ResourceCard
              key={caseStudy.customerSlug}
              href={`/customers/${caseStudy.customerSlug}`}
              linkAriaLabel={`Read ${caseStudy.title}.`}
              title={caseStudy.title}
              titleElement="h3"
              actionLabel="Read the story"
              image={
                <Flex display={["none", "block"]} pos="relative" justify="center" align="center" width="100%">
                  <AspectRatio
                    ratio={16 / 9}
                    width="100%"
                    pos="relative"
                    _after={{
                      position: "absolute",
                      content: "''",
                      opacity: 0.85,
                      height: "100%",
                      w: "100%",
                      bg: "space",
                      transition: "100ms all",
                      top: 0,
                      left: 0,
                    }}
                    _groupHover={{
                      _after: {
                        opacity: 0.9,
                      },
                    }}
                  >
                    <Image
                      src={caseStudy.imageUrl + "?w=800"}
                      alt={caseStudy.title + " background."}
                      layout="fill"
                      objectFit="cover"
                    />
                  </AspectRatio>

                  <Flex pos="absolute" inset={0} align="center" justify="center" filter="brightness(0) invert(1)">
                    <Box pos="relative" w="50%" h="50%">
                      <Image
                        src={`${caseStudy.customerLogoMono ?? caseStudy.customerLogo}?w=400`}
                        alt={caseStudy.customerName + "."}
                        objectFit="contain"
                        layout="fill"
                      />
                    </Box>
                  </Flex>
                </Flex>
              }
            />
          ))}
        </SimpleGrid>
      </Section>
    </Page>
  );
};

export default Customers;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, caseStudies, customerRows] = await Promise.all([
    getPage("/customers"),
    getAnnouncementBanner(),
    getSimpleCaseStudies(),
    getStackedLogos(),
  ]);

  const heroCaseStudy = caseStudies.find(({ customerSlug }) => customerSlug === "wmg");

  if (!page || !heroCaseStudy) {
    return {
      notFound: true,
    };
  }

  const featuredCaseStudyCustomers = new Set(["cars", "whoop", "chime"]);

  const featuredCaseStudies = caseStudies.filter(({ customerSlug }) => featuredCaseStudyCustomers.has(customerSlug));
  const remainingCaseStudies = caseStudies.filter(
    ({ customerSlug }) => customerSlug !== "wmg" && !featuredCaseStudyCustomers.has(customerSlug),
  );

  return {
    props: {
      page,
      announcementBanner,
      heroCaseStudy,
      featuredCaseStudies,
      remainingCaseStudies,
      customerRows,
    },
    revalidate: 60,
  };
};
